
import {Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import {FileOpener} from '@ionic-native/file-opener';
import {store} from '@/store';
import {addFileUri, clearFileUris, getFileUris} from '@/storage/DataStorage';
import {AlertBuilder} from '@/utilities/AlertBuilder';
import {createLoadSpinner} from '@/services/ShareService';
import { Capacitor } from '@capacitor/core';
import {
    DeleteApp,
    StorageLocationPicker as StorageLocationPickerPlugin,
} from '@/delete-app-plugin/definitions';
import { Share } from '@capacitor/share';
import {FileSharer} from '@byteowls/capacitor-filesharer';


const checkPermissions = async () => {
    const permissionStatus = await Filesystem.checkPermissions();
    return permissionStatus.publicStorage === 'granted';
  };
  
const requestPermissions = async () => {
    const permissionStatus = await Filesystem.requestPermissions();
    return permissionStatus.publicStorage === 'granted';
};

export async function  checkAndRequestStoragePermission() {
    if (!(await checkPermissions())) {
      return await requestPermissions();
    }
    return true;
}

/** Clears suppress flag after app resume has had a chance to consume it (see App.vue appStateChange). */
function scheduleClearSuppressCalculatorOnResume() {
    setTimeout(() => {
        void store.dispatch("setSuppressSafetyCalculatorOnResume", false);
    }, 800);
}

async function handleIosFileSave(filename: string, content: string, plainText: boolean, mimeType: string) {
    await store.dispatch("setSuppressSafetyCalculatorOnResume", true);
    try {
        const file = await Filesystem.writeFile({
            path: `${filename}`,
            data: content,
            encoding: plainText ? Encoding.UTF8 : undefined,
            directory: Directory.Cache,
        });
        await FileOpener.showOpenWithDialog(file.uri, mimeType);
    } finally {
        scheduleClearSuppressCalculatorOnResume();
    }
}

async function handleAndroidFileSave(filename: string, content: string, plainText: boolean, mimeType: string, useDeviceApk: boolean) {
    if(await checkAndRequestStoragePermission()){
        await store.dispatch("setSuppressSafetyCalculatorOnResume", true);
        try {
            await store.dispatch("setShouldExitApp", false);
            const { uri } = await StorageLocationPickerPlugin.save({ filename: filename, content: content, plainText: plainText, mimeType: mimeType, useDeviceApk: useDeviceApk });
            await addFileUri(uri);
            await store.dispatch("setShouldExitApp", true);
        } finally {
            scheduleClearSuppressCalculatorOnResume();
        }
    }
}

export async function handleAndroidFileSelectToDelete(mimeType: string) {
    
    if(await checkAndRequestStoragePermission()){
        await store.dispatch("setShouldExitApp", false);
        const { uri } = await StorageLocationPickerPlugin.navigate({ mimeType: mimeType});
        await StorageLocationPickerPlugin.delete({uris: [uri]});
        await store.dispatch("setShouldExitApp", true);
    }   
}

export async function deleteAndroidFiles() {
    const loading = await createLoadSpinner();
    const language = store.getters.getLocaleMessages();
    await loading.present();
    const uris = await getFileUris();
    try {
        await StorageLocationPickerPlugin.delete({uris: uris});
        await clearFileUris();
    } catch {
        const alert = new AlertBuilder('custom-alert', language.application.errorDeletingFiles).addCancelButton();
        alert.present();
    } finally {
        await loading.dismiss();
    }
}

export async function writeFile(filename: string, content: string, plainText: boolean, mimeType: string, useDeviceApk: boolean) {
    if (store.getters.platform() === "ios") {
        await handleIosFileSave(filename, content, plainText, mimeType);
    } else if (store.getters.isPwa()) {
        await shareFile(filename, content, plainText, mimeType);
    } else {
        if(await checkAndRequestStoragePermission()){
            await handleAndroidFileSave(filename, content, plainText, mimeType, useDeviceApk);
        }
    }
}



export async function writeFileToDocumentsDirectory(fileName: string, data: string) {
    if(await checkAndRequestStoragePermission()){
        try {
            await Filesystem.writeFile({
                path: fileName,
                data: data,
                directory: Directory.Documents,
                encoding: Encoding.UTF8
            });
            try {
                const result = await Filesystem.readdir({
                path: '', // Root path of the directory
                directory: Directory.Data
                });
            } catch (e) {
                console.error('Debug: Unable to read directory', e);
            }
        } catch (error) {
            console.error('Debug: Error writing file to documents directory', error);
        }
    }   
}


export async function dataDirectoryFileExists(filename: string) {
    try {
        await Filesystem.stat({
          path: filename,
          directory: Directory.Data
        });
        return true;
      } catch (error: any) {
        if (error.message === 'File does not exist') {
          return false;
        } else {
          console.error('Debug: Error checking file existence:', error);
          return false;
        }
    }
}

// ============================================================================
// BATCHED LOGGING SYSTEM
// Instead of writing each log entry to filesystem immediately (which overwhelms
// the WebView with I/O operations), we batch entries in memory and write 
// periodically or when batch is full.
// ============================================================================

interface LogEntry {
    entryType: string;
    timestamp: string;
    logData: string;
    errorMessage: string;
    platform: string;
}

// In-memory log buffer
// CRITICAL: No automatic filesystem writes during initialization!
// Filesystem I/O during startup causes WebView crashes on iPhones.
// Logs are ONLY written to filesystem when explicitly flushed (on app background).
let logBuffer: LogEntry[] = [];
const MAX_BUFFER_SIZE = 500; // Keep last 500 entries in memory
let isFlushingLogs = false;

/**
 * Flush all buffered log entries to the filesystem
 * ONLY called explicitly (e.g., when app backgrounds)
 * NOT called automatically during initialization to avoid WebView crashes
 */
export const flushLogs = async () => {
    // Prevent concurrent flushes
    if (isFlushingLogs || logBuffer.length === 0) {
        return;
    }
    
    isFlushingLogs = true;
    
    // Take all entries from buffer
    const entriesToWrite = [...logBuffer];
    logBuffer = [];
    
    try {
        const fileName = "applog.txt";
        const batchData = entriesToWrite.map(e => JSON.stringify(e)).join('\n');
        
        // Read existing content first (append mode to preserve history)
        let existingContent = '';
        try {
            const file = await Filesystem.readFile({
                path: fileName,
                directory: Directory.Data,
                encoding: Encoding.UTF8
            });
            existingContent = typeof file.data === 'string' ? file.data : await file.data.text();
        } catch {
            // File doesn't exist yet - that's fine
        }
        
        // Append new batch and write
        const updatedContent = existingContent + (existingContent ? '\n' : '') + batchData;
        await Filesystem.writeFile({
            path: fileName,
            data: updatedContent,
            directory: Directory.Data,
            encoding: Encoding.UTF8
        });
    } catch (error: any) {
        console.error("Error writing batched log to file: " + error.message);
        // Put entries back in buffer if write failed (but limit size)
        logBuffer = [...entriesToWrite.slice(-MAX_BUFFER_SIZE), ...logBuffer].slice(-MAX_BUFFER_SIZE);
    } finally {
        isFlushingLogs = false;
    }
};

/**
 * Add a log entry to the buffer
 * NO automatic filesystem writes - only buffer in memory
 */
const addLogEntry = (logData: string, errorData: string, entryType: string) => {
    const entry: LogEntry = {
        entryType: entryType,
        timestamp: new Date().toISOString(),
        logData: logData,
        errorMessage: errorData,
        platform: store.state.platform || "unknown platform",
    };
    
    logBuffer.push(entry);
    
    // Keep buffer from growing indefinitely (circular buffer behavior)
    if (logBuffer.length > MAX_BUFFER_SIZE) {
        logBuffer = logBuffer.slice(-MAX_BUFFER_SIZE);
    }
    // NO automatic flush - filesystem writes only on explicit flushLogs() call
};

/**
 * Legacy function - now adds to batch instead of writing immediately
 */
const writeErrorToFile = async (logData: string = "", errorData: string = "", entryType: string = "error") => {
    addLogEntry(logData, errorData, entryType);
};

export const logError = async (error: any, logData: string = "") => {
    const errorMessage = error.message || error.toString();
    console.error("error: " + logData + " - " + errorMessage);
    
    // Errors are important - add to buffer (will be flushed with batch)
    addLogEntry(logData, errorMessage, "error");
};

export const logInfo = async (logData: string, level: number = 1) => {
    // Always log to console for debugging
    console.log("info: " + logData);
    
    // Add to buffer based on configured log level
    // During early init, logLevel may be undefined (treat as 0 = log everything to buffer)
    const configuredLevel = store.state.logLevel ?? 0;
    if (configuredLevel >= level) {
        addLogEntry(logData, "", "info");
    }
    return;
}

export async function readJSONFileFromDataDirectory(filename: string, plainText: boolean) {
    try {
        logInfo(`[readJSONFileFromDataDirectory] Reading filename ${filename} plainText: ${plainText}`,2);
        const result = await Filesystem.readFile({
            path: filename,
            directory: Directory.Data,
            encoding: Encoding.UTF8
        });

        let jsonData: string;

        if (result.data instanceof Blob) {
            // Convert Blob to string
            const text = await result.data.text();
            jsonData = text;
            logInfo(`[readJSONFileFromDataDirectory] jsonData was a Blob, converted to string , plainText: ${plainText}`, 2);
        } else {
            jsonData = result.data; // Already a string
        }
        if(!plainText){
            const fileJson = await JSON.parse(jsonData);
            logInfo(`[readJSONFileFromDataDirectory] jsonData parsed to object` , 2);

            return fileJson;
        }else{
            return jsonData;
        }
    } catch (error) {
        console.error('Error reading local json file:', error);
        return {};
    }
}

//TODO: Look for replacement method / library as byteowls package is no longer being updated and considered deprecated
/**
 * Deprecated 
 */
export async function shareFile(filename: string, content: string, plainText: boolean, mimeType: string) {
    await store.dispatch("setSuppressSafetyCalculatorOnResume", true);
    try {
        await FileSharer.share({
            filename: filename,
            base64Data: plainText ? btoa(content) : content,
            contentType: mimeType,
        });
    } finally {
        scheduleClearSuppressCalculatorOnResume();
    }
}

export async function shareFileViaURI(filePath: string) {
    await store.dispatch("setSuppressSafetyCalculatorOnResume", true);
    try {
        await Share.share({
            url: filePath,
        });
    } finally {
        scheduleClearSuppressCalculatorOnResume();
    }
}

export async function deleteApp() {
    if (store.getters.platform() === "android") {
        await deleteAndroidFiles();
    }
    await DeleteApp.deleteApp();
}

export const shareErrorLog = async () => {
    try {
        const fileName = "applog.txt";
        if (await dataDirectoryFileExists(fileName)) {
            const file = await Filesystem.copy({
                from: fileName,
                to: fileName,
                directory: Directory.Data,
                toDirectory: Directory.Cache,
            });

            await shareFileViaURI(file.uri);
            
            try {
                await writeFileToDataDirectory(fileName, "");
                await Filesystem.deleteFile({
                    path: fileName,
                    directory: Directory.Cache,
                });
            } catch (error) {
                console.error('Error clearing applog.txt', error);
            }
        } else {
            console.error("Error log file not found");
        }
    } catch (error: any) {
        console.error("Error sharing error log: " + error.message);
    }
}


export async function writeFileToDataDirectory(fileName: string, data: string) {
    try {
        await Filesystem.writeFile({
            path: fileName,
            data: data,
            directory: Directory.Data,
            encoding: Encoding.UTF8
        });
        try {
            const result = await Filesystem.readdir({
              path: '', // Root path of the directory
              directory: Directory.Data
            });
          } catch (e) {
            console.error('Debug: Unable to read directory', e);
        }
    } catch (error) {
        console.error('Debug: Error writing file to data directory', error);
        logInfo(`[writeFileToDataDirectory] Error writing file ${fileName} to data directory: ${error}`, 1);
    }
}

export async function writeTextFileToDataDirectory(fileName: string, data: string) {
    try {
        // Check if the file exists
        let existingContent = '';
        try {
            const file = await Filesystem.readFile({
                path: fileName,
                directory: Directory.Data,
                encoding: Encoding.UTF8
            });
            if (typeof file.data === 'string') {
                existingContent = file.data; // Store existing content if the file exists
            } else {
                // Handle the Blob case if necessary
                existingContent = await file.data.text(); // Convert Blob to string
            }
        } catch (readError) {
            console.error("Superfluous error while reading file: ");
        }
        // Append new data
        const updatedContent = existingContent + data;
        // Write (overwrite with the updated content)
        await Filesystem.writeFile({
            path: fileName,
            data: updatedContent,
            directory: Directory.Data,
            encoding: Encoding.UTF8
        });
        //console.log('Debug: log file written successfully');
    } catch (error) {
        console.error('Debug: Error writing to file', error);
    }
}

export async function ensureFileFromAssets(dataFilePath: string, assetPath: string) {
  try {
    logInfo(`[ensureFileFromAssets] Checking for file in data: ${dataFilePath}` , 2)
    //console.log(`[ensureFileFromAssets] Checking for file in data: ${dataFilePath}`);
    // Check if the file already exists in the data directory
    await Filesystem.stat({
      path: dataFilePath,
      directory: Directory.Data
    });
    return;
  } catch {
    // File does not exist in data directory
    logInfo(`[ensureFileFromAssets] File not found in data: ${dataFilePath}. Attempting to copy from assets...`, 2);
    //console.log(`[ensureFileFromAssets] File not found in data: ${dataFilePath}. Attempting to copy from assets...`);
  }

  // Try to load it from assets
  const assetUrl = Capacitor.convertFileSrc(assetPath);

  try{
        const response = await fetch(assetUrl);

        if (response.ok) {
            const data = await response.text();
            writeFileToDataDirectory(dataFilePath, data);
            logInfo(`[ensureFileFromAssets] Copied ${assetPath} to data as ${dataFilePath}` , 1);
            //console.log(`[ensureFileFromAssets] Copied ${assetPath} to data as ${dataFilePath}`);
        } else {
            logInfo(`[ensureFileFromAssets] Asset file not found: ${assetPath}` , 1);
            //console.log(`[ensureFileFromAssets] Asset file not found: ${assetPath}`);
        }
    } catch (error: any) {
        logInfo(`[ensureFileFromAssets] Error fetching asset file: ${error.message}` , 1);
    }
}

/**
 * Copy an image from assets to Directory.Data (for PNG/binary assets).
 * Skips copy if file already exists in Directory.Data.
 */
export async function ensureImageFromAssets(dataFilePath: string, assetPath: string): Promise<boolean> {
    try {
        await Filesystem.stat({
            path: dataFilePath,
            directory: Directory.Data
        });
        return true;
    } catch {
        logInfo(`[ensureImageFromAssets] File not found in data: ${dataFilePath}. Attempting to copy from assets...`, 2);
    }
    try {
        const assetUrl = Capacitor.convertFileSrc(assetPath);
        const response = await fetch(assetUrl);
        if (!response.ok) {
            logInfo(`[ensureImageFromAssets] Asset file not found: ${assetPath}`, 1);
            return false;
        }
        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        await Filesystem.writeFile({
            path: dataFilePath,
            data: base64,
            directory: Directory.Data,
            encoding: 'base64' as Encoding
        });
        logInfo(`[ensureImageFromAssets] Copied ${assetPath} to data as ${dataFilePath}`, 1);
        return true;
    } catch (error: any) {
        logInfo(`[ensureImageFromAssets] Error copying asset: ${error.message}`, 1);
        return false;
    }
}

/**
 * Write an image Blob to Directory.Data (e.g. appIcon.png from S3).
 * Used when downloading a new app icon during bundle update.
 */
export async function writeImageBlobToDataDirectory(filename: string, blob: Blob): Promise<boolean> {
    try {
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        await Filesystem.writeFile({
            path: filename,
            data: base64,
            directory: Directory.Data,
            encoding: 'base64' as Encoding
        });
        logInfo(`[writeImageBlobToDataDirectory] Wrote ${filename} to Directory.Data`, 1);
        return true;
    } catch (error: any) {
        logInfo(`[writeImageBlobToDataDirectory] Error writing ${filename}: ${error.message}`, 1);
        return false;
    }
}

/**
 * Get a displayable URL for a file in Directory.Data (e.g. for use in img src).
 * Uses getUri + convertFileSrc so the WebView can load the file. Returns null if file doesn't exist.
 */
export async function getDataDirectoryFileUrl(path: string): Promise<string | null> {
    try {
        const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
        return Capacitor.convertFileSrc(uri);
    } catch {
        return null;
    }
}

/**
 * Read an image file from Directory.Data and return a data URI (e.g. for PNG).
 */
export async function readImageDataUriFromDataDirectory(filename: string, mimeType: string = 'image/png'): Promise<string | null> {
    try {
        const result = await Filesystem.readFile({
            path: filename,
            directory: Directory.Data,
            encoding: 'base64' as Encoding
        });
        const base64 = typeof result.data === 'string' ? result.data : (result.data instanceof Blob ? null : String(result.data));
        if (!base64) return null;
        return `data:${mimeType};base64,${base64}`;
    } catch {
        return null;
    }
}

/**
 * Read a text file from the data directory
 * @param filename The name of the file to read
 * @returns The file contents as a string, or null if file doesn't exist
 */
export async function readTextFileFromDataDirectory(filename: string): Promise<string | null> {
    try {
        logInfo(`[readTextFileFromDataDirectory] Reading file: ${filename}`, 2);
        const result = await Filesystem.readFile({
            path: filename,
            directory: Directory.Data,
            encoding: Encoding.UTF8
        });
        
        let textData: string;
        if (result.data instanceof Blob) {
            textData = await result.data.text();
        } else {
            textData = result.data;
        }
        
        logInfo(`[readTextFileFromDataDirectory] Successfully read file: ${filename}`, 2);
        return textData;
    } catch (error) {
        logInfo(`[readTextFileFromDataDirectory] File not found or error reading: ${filename}`, 2);
        return null;
    }
}

/**
 * Check if a cached file is still valid by comparing its version
 * @param filename The cached file name
 * @param versionProperty The property name in the JSON that contains the version
 * @param requiredVersion The version required
 * @returns True if cached file has the required version
 */
export async function isCachedFileValid(
    filename: string, 
    versionProperty: string, 
    requiredVersion: string | number
): Promise<boolean> {
    try {
        const fileContent = await readJSONFileFromDataDirectory(filename, false);
        if (!fileContent || Object.keys(fileContent).length === 0) {
            return false;
        }
        
        const fileVersion = fileContent[versionProperty];
        if (!fileVersion) {
            return false;
        }
        
        const isValid = String(fileVersion) === String(requiredVersion);
        logInfo(
            `[isCachedFileValid] ${filename}: cached version=${fileVersion}, required=${requiredVersion}, valid=${isValid}`, 
            1
        );
        return isValid;
    } catch (error) {
        logInfo(`[isCachedFileValid] Error checking ${filename}: ${error}`, 2);
        return false;
    }
}

